/**
 * CetusEd NextGen — session orchestration.
 *
 * Composes the stateless auth transport (api/auth-endpoints) with the session
 * store into the behaviours React and the API client call:
 *   - refreshAccess()   — single-flight silent token rotation
 *   - authedFetch()     — Bearer + one-shot 401→refresh→retry, FAIL CLOSED
 *   - fetchCurrentUser()— GET /api/auth/me through authedFetch
 *   - signIn / signOut / restoreSession — the lifecycle transitions
 *
 * ⛔2 FAIL CLOSED: every path that cannot prove a valid session ENDS it
 * (sessionStore.clear) rather than leaving stale tokens or guessing. A failed
 * refresh is an expected end-of-session, not a bug — it is handled (logged +
 * clear), never swallowed and never rethrown into a false "still logged in".
 */
import { API_BASE } from '../../api/config'
import { unwrap } from '../../api/response'
import { postLogin, postRefresh, postLogout } from '../../api/auth-endpoints'
import type { CurrentUser, LoginInput } from '../../api/contract'
import { sessionStore } from './session-store'
import type { SessionUser } from './types'

/** A fetch that this module drives with the current access token. */
type Fetcher = (token: string | null) => Promise<Response>

// Single-flight: concurrent 401s share ONE refresh, never a thundering herd.
let refreshInFlight: Promise<boolean> | null = null

/**
 * Attempt to rotate the refresh token for a fresh access token. Returns whether
 * a new access token is now held. Never throws — a failed refresh is a normal
 * end-of-session; the caller decides to clear. Coalesces concurrent callers.
 */
export function refreshAccess(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight
  const refreshToken = sessionStore.getRefreshToken()
  if (!refreshToken) return Promise.resolve(false)

  refreshInFlight = (async () => {
    try {
      const tokens = await postRefresh(refreshToken, sessionStore.getAccessToken())
      sessionStore.setTokens({ accessToken: tokens.accessToken, refreshToken: tokens.refreshToken })
      return true
    } catch (err) {
      // Expected when the refresh token is expired/revoked, or (today) when the
      // refresh route rejects a request that carries no valid access token
      // (TASK-00082). Handled: report and report failure so the caller clears.
      console.warn('[CetusEd] Token refresh failed — session will end', err)
      return false
    } finally {
      refreshInFlight = null
    }
  })()

  return refreshInFlight
}

/**
 * Run `doFetch` with the current access token; on a 401, attempt ONE refresh
 * and retry once. If refresh is impossible or the retry still 401s, clear the
 * session (fail closed) and return the last response so the caller's unwrap
 * throws a real AppError.
 */
async function authedFetch(doFetch: Fetcher): Promise<Response> {
  let res = await doFetch(sessionStore.getAccessToken())
  if (res.status !== 401) return res

  const refreshed = await refreshAccess()
  if (!refreshed) {
    sessionStore.clear()
    return res
  }

  res = await doFetch(sessionStore.getAccessToken())
  if (res.status === 401) sessionStore.clear()
  return res
}

/**
 * The fetch the hono `hc` v1 client uses: attaches Bearer + shares the 401
 * refresh path. hc passes an absolute URL it built from API_BASE, so this only
 * decorates headers and never rewrites the target.
 */
export const hcAuthedFetch = (
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> =>
  authedFetch((token) => {
    const headers = new Headers(init?.headers)
    if (token) headers.set('Authorization', `Bearer ${token}`)
    return fetch(input, { ...init, headers })
  })

/** GET /api/auth/me through the authed/refresh path; non-2xx throws (unwrap). */
export async function fetchCurrentUser(): Promise<CurrentUser> {
  const res = await authedFetch((token) => {
    const headers = new Headers()
    if (token) headers.set('Authorization', `Bearer ${token}`)
    return fetch(`${API_BASE}/api/auth/me`, { headers })
  })
  return unwrap<CurrentUser>(res)
}

/**
 * Present credentials, and on success establish the session. The returned
 * value is the REAL API result — there is no fabricated user (⛔4). A failed
 * login throws (from unwrap) and establishes nothing.
 */
export async function signIn(input: LoginInput): Promise<SessionUser> {
  const result = await postLogin(input)
  const user: SessionUser = result.user
  sessionStore.setSession(user, {
    accessToken: result.accessToken,
    refreshToken: result.refreshToken,
  })
  return user
}

/**
 * End the session. Best-effort server revocation, but the LOCAL session is
 * cleared no matter what the server says — a user asking to sign out must end
 * up signed out even if the revocation call fails.
 */
export async function signOut(): Promise<void> {
  const refreshToken = sessionStore.getRefreshToken()
  const accessToken = sessionStore.getAccessToken()
  try {
    if (refreshToken) await postLogout(refreshToken, accessToken)
  } catch (err) {
    // Handled, not swallowed: rethrowing would abort before we clear and leave
    // tokens live — the opposite of "log me out". Clear happens in finally.
    console.warn('[CetusEd] Server logout failed; clearing local session anyway', err)
  } finally {
    sessionStore.clear()
  }
}

/**
 * Boot-time session restore. With an access token already in memory, we are
 * authenticated. Otherwise, if a persisted refresh token exists, attempt ONE
 * refresh; success authenticates, failure clears (fail closed). No refresh
 * token → unauthenticated. Idempotent-safe to call once from the provider.
 *
 * NOTE (honest current limitation): the refresh route is not yet public
 * (TASK-00082), so a cold reload with only a refresh token cannot renew and
 * will land on /login until that route ships. This code is already correct for
 * that day — it does not need to change.
 */
export async function restoreSession(): Promise<void> {
  if (sessionStore.getAccessToken()) {
    sessionStore.setStatus('authenticated')
    return
  }
  if (!sessionStore.getRefreshToken()) {
    sessionStore.setStatus('unauthenticated')
    return
  }
  sessionStore.setStatus('restoring')
  const refreshed = await refreshAccess()
  if (refreshed) {
    sessionStore.setStatus('authenticated')
  } else {
    sessionStore.clear()
  }
}
